import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Card, Divider, Popover, Table, TablePaginationConfig } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useDownloadDebtAccountsExport, useGetDebtAccountsReport } from "../../../../../common/api/queries";
import { TableSizes } from "../../../../../common/constants";
import { ExportFileType } from "../../../../../common/enums";
import { SuperiorAgentsTable } from "../../../../agent/components/tables/SuperiorAgentsTable";
import { AgentTypeTagWithName } from "../../../../agent/components/tags/AgentTypeTagWithName";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { formatAgentIdNumber } from "../../../../agent/utils";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import { DebtAccountBalance, DebtAccountsReportFilterPageRequest } from "../../types";

type Props = {
  searchParams: DebtAccountsReportFilterPageRequest;
};

export const DebtAccountsReportTable = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const { data } = useGetDebtAccountsReport(searchParams);
  const downloadDebtAccountsExport = useDownloadDebtAccountsExport();

  const columns: ColumnsType<DebtAccountBalance> = [
    {
      key: "agentName",
      title: t("common.agent"),
      width: 260,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <AgentTypeTagWithName agent={record.agent} hideIdNumber ellipsis showLink showRepresentatives />
      ),
    },
    {
      key: "idNumber",
      title: t("agent.attrs.idNumber"),
      width: 120,
      render: (_, record) => formatAgentIdNumber(record.agent),
    },
    {
      key: "commissionsSettingsLevel",
      title: t("agent.helpers.commissionsSettingsLevel"),
      width: 120,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.level?.code}</Ellipsis>,
    },
    {
      key: "commissionsSettingsRank",
      title: t("agent.helpers.commissionsSettingsRank"),
      width: 160,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.rank?.name}</Ellipsis>,
    },
    {
      key: "superiorAgents",
      width: 40,
      align: "center",
      render: (_, record) => (
        <Popover
          title={t("agent.titles.superiorAgents")}
          styles={{ root: { maxWidth: 580 } }}
          content={<SuperiorAgentsTable agent={record.agent} />}
        >
          <span>
            <AntIcon color="blue" type="cluster" />
          </span>
        </Popover>
      ),
    },
    {
      key: "status",
      title: (
        <div>
          <div>{isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}</div>
          {isTopAgentPlus ? <div>{t("agent.helpers.agentSecondaryStatusSFAPlusLabel")}</div> : undefined}
        </div>
      ),
      width: 210,
      ellipsis: { showTitle: false },
      render: (_, record) => (
        <>
          <Ellipsis>{t(`agent.enums.agentStatus.${record.agent.status}`)}</Ellipsis>

          {isTopAgentPlus && record.agent.secondaryStatus ? (
            <>
              <br />
              <Ellipsis>{t(`agent.enums.agentSecondaryStatus.${record.agent.secondaryStatus}`)}</Ellipsis>
            </>
          ) : undefined}
        </>
      ),
    },
    {
      key: "balance",
      title: t("commissions.debtAccount.helpers.balance"),
      align: "right",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <span className={record.balance < 0 ? "highlighted-text" : undefined}>
          {formatLocaleCurrency(record.balance)}
        </span>
      ),
    },

    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 90,
      render: (_, record) => (
        <ActionTextIcon
          path={generatePath(AGENT_ROUTE_PATHS.commissions.to, { id: record.agent.id }) + "?tab=debt-account"}
          icon="eye"
          color="blue"
          text={t("common.show")}
        />
      ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            pageIndex: pagination.current ? pagination.current - 1 : undefined,
          },
          append: true,
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card className="card-box">
      <div className={numberOrZero(data?.totalElementsCount) ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() => downloadDebtAccountsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })}
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() => downloadDebtAccountsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })}
        />
      </div>

      <Table<DebtAccountBalance>
        {...tableStandardProps()}
        columns={columns}
        rowKey={(record) => record.agent.id}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={data?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={handleTableChange}
      />
    </Card>
  );
};
